import styled, { css, keyframes } from 'styled-components';
import { color, duration, ease, radius } from '../../../theme/tokens';

/* ------------------------------------------------------------------ *
 * Keyframes — the shared motion vocabulary of the storefront.
 * ------------------------------------------------------------------ */

export const fadeUp = keyframes`
  from { opacity: 0; transform: translate3d(0, 26px, 0); }
  to   { opacity: 1; transform: none; }
`;

export const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

/** Used by self-dismissing surfaces (admin confirmations) to bow out quietly. */
export const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

export const scaleIn = keyframes`
  from { opacity: 0; transform: scale(0.94); }
  to   { opacity: 1; transform: scale(1); }
`;

export const floaty = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0); }
  50%      { transform: translate3d(0, -12px, 0); }
`;

export const shimmer = keyframes`
  0%   { background-position: -420px 0; }
  100% { background-position: 420px 0; }
`;

export const kenBurns = keyframes`
  from { transform: scale(1); }
  to   { transform: scale(1.08); }
`;

export const marquee = keyframes`
  from { transform: translate3d(0, 0, 0); }
  to   { transform: translate3d(-50%, 0, 0); }
`;

export const drawCheck = keyframes`
  from { stroke-dashoffset: 60; }
  to   { stroke-dashoffset: 0; }
`;

export const pulseRing = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(74, 112, 97, 0.35); }
  70%  { box-shadow: 0 0 0 18px rgba(74, 112, 97, 0); }
  100% { box-shadow: 0 0 0 0 rgba(74, 112, 97, 0); }
`;

export const shake = keyframes`
  0%, 100% { transform: translate3d(0, 0, 0); }
  20%      { transform: translate3d(-6px, 0, 0); }
  40%      { transform: translate3d(6px, 0, 0); }
  60%      { transform: translate3d(-4px, 0, 0); }
  80%      { transform: translate3d(4px, 0, 0); }
`;

/** Respect the OS "reduce motion" setting everywhere in one place. */
export const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
    transform: none !important;
    opacity: 1 !important;
  }
`;

/* ------------------------------------------------------------------ *
 * Reusable animated surfaces
 * ------------------------------------------------------------------ */

/** Card that lifts, brightens and sharpens its shadow on hover. */
export const LiftCard = styled.div`
  transition:
    transform ${duration.base} ${ease.out},
    box-shadow ${duration.base} ${ease.out};
  will-change: transform;

  &:hover {
    transform: translate3d(0, -8px, 0);
  }

  ${reducedMotion};
`;

/** Skeleton block used while products are loading. */
export const Shimmer = styled.div<{ $height?: string; $radius?: string }>`
  height: ${({ $height }) => $height || '18px'};
  border-radius: ${({ $radius }) => $radius || radius.sm};
  background: linear-gradient(
    90deg,
    ${color.creamDeep} 0%,
    ${color.sandLight} 42%,
    ${color.creamDeep} 84%
  );
  background-size: 840px 100%;
  animation: ${shimmer} 1.5s linear infinite;
  ${reducedMotion};
`;

/** Thin uppercase label used above section titles. */
export const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-size: 0.72rem;
  letter-spacing: 0.34em;
  text-transform: uppercase;
  color: ${color.forest};
  font-weight: 500;

  &::before {
    content: '';
    width: 32px;
    height: 1px;
    background: ${color.forest};
    opacity: 0.5;
  }
`;
